import { SCALE_EMOJIS } from '../constants/painConstants';
import { CRYSTAL_ICONS, type CrystalIconKey } from './CrystalIcons';

const FACES_ICON_KEYS: Record<number, CrystalIconKey> = {
  0: 'faces-0',
  2: 'faces-2',
  4: 'faces-4',
  6: 'faces-6',
  8: 'faces-8',
  10: 'faces-10',
};

export type SliderConfig = {
  label: React.ReactNode;
  value: number | null;
  setValue: (v: number) => void;
  min?: number;
  max?: number;
  leftLabel: string;
  rightLabel: string;
  color: string;
  gradient?: string;
  // When omitted, the slider renders Citrine CrystalEmoji faces via SCALE_MOOD_IDS.
  // Pass an explicit Record to opt back into the legacy unicode emoji preview, or pass null to suppress.
  emojiMap?: Record<number, React.ReactNode> | null;
};

const EMOJI_MAP_SENTINEL: Record<number, React.ReactNode> = {};

export default function SliderField({ label, value, setValue, min = 0, max = 10, leftLabel, rightLabel, color, emojiMap = EMOJI_MAP_SENTINEL }: SliderConfig) {
  const nums = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  const useFacesIcons = emojiMap === EMOJI_MAP_SENTINEL || emojiMap === SCALE_EMOJIS;
  const showEmojiRow = emojiMap != null;
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>{label}</label>
        <div style={{ minWidth: 30, height: 30, background: value !== null ? color : 'var(--color-bg-surface)', color: value !== null ? '#fff' : 'var(--color-text-secondary)', fontWeight: 700, fontSize: 'var(--fs-sm)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: value !== null ? `0 2px 8px color-mix(in srgb, ${color} 33%, transparent)` : 'none' }}>{value !== null ? value : '—'}</div>
      </div>
      {showEmojiRow && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${nums.length}, 1fr)`, gap: 3, marginBottom: 3, alignItems: 'end' }}>
          {nums.map(n => {
            const showEmoji = n % 2 === 0;
            const nearestEven = value !== null ? Math.round(value / 2) * 2 : -1;
            const isActive = showEmoji && n === nearestEven;
            if (useFacesIcons) {
              const iconKey = FACES_ICON_KEYS[n];
              const faceSize = isActive ? 52 : 38;
              if (showEmoji && iconKey) {
                const FaceIcon = CRYSTAL_ICONS[iconKey];
                return (
                  <div key={n} style={{ textAlign: 'center', lineHeight: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', transition: 'opacity 0.15s', opacity: isActive ? 1 : 0.7 }}>
                    <FaceIcon size={faceSize} ariaLabel="" />
                  </div>
                );
              }
              return <div key={n} />;
            }
            // Legacy unicode emoji fallback (consumers that pass an explicit emojiMap record).
            const map = emojiMap as Record<number, React.ReactNode>;
            return (
              <div key={n} style={{ textAlign: 'center', lineHeight: 1, fontSize: isActive ? 'var(--fs-lg)' : 'var(--fs-sm)', transition: 'font-size 0.15s', filter: isActive ? 'none' : 'opacity(0.45)' }}>
                {showEmoji ? (map[n] ?? '') : ''}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${nums.length}, 1fr)`, gap: 3 }}>
        {nums.map(n => {
          const isSelected = value !== null && n === value;
          const isFilled = value !== null && n < value;
          return (
            // eslint-disable-next-line cw/no-inline-button-style -- SliderField number tile — color-prop driven scale-grid tile (NRS slider field component)
            <button
              key={n}
              onClick={() => setValue(n)}
              style={{ height: 38, borderRadius: 'var(--radius-sm)', border: isSelected ? `2px solid ${color}` : `2px solid ${isFilled ? `color-mix(in srgb, ${color} 33%, transparent)` : 'var(--color-border, color-mix(in srgb, var(--color-text-primary) 9%, transparent))'}`, background: isSelected ? color : isFilled ? `color-mix(in srgb, ${color} 19%, transparent)` : 'var(--color-bg-surface, color-mix(in srgb, var(--color-text-primary) 2%, transparent))', color: isSelected ? '#fff' : isFilled ? color : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: isSelected ? 800 : 400, cursor: 'pointer', transition: 'all 0.1s', boxShadow: isSelected ? `0 2px 8px color-mix(in srgb, ${color} 27%, transparent)` : 'none' }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 5 }}>
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}
